/**
 * Isometric primitive icons - 24x24 viewBox, 30 degree isometric projection.
 *
 * One primitive per category, varied by size and colour only:
 *   cube (tall / plain / slab / block), sphere, pyramid.
 *
 * `svg`   single path, faces separated by hairline seams. Drop-in replacement
 *         for the old field - one colour, still reads as 3-D.
 * `faces` same geometry split by surface, each with a shade offset. Render
 *         these for tonal depth: top lit, left mid, right dark.
 *
 * Faces are listed in painter order.
 */

export interface IconFace {
    /** SVG path data, 24x24 viewBox. */
    d: string;
    /** -1 = black, 0 = base colour, +1 = white. Applied to IconConfig.color. */
    shade: number;
}

export interface IconConfig {
    color: string;
    icon: string;
    svg: string;
    faces: IconFace[];
}

export const ICON_VIEW_BOX = '0 0 24 24';

export const ICON_REGISTRY: Record<string, IconConfig> = {
    // tall cube - 0.72 footprint, 1.75 high
    "BUILDING": {
        color: "#f7bd95",
        icon: "cube-tall",
        svg: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55ZM7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41ZM16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z",
        faces: [
            { shade: 0.18, d: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55Z" },
            { shade: -0.12, d: "M7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41Z" },
            { shade: -0.34, d: "M16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z" },
        ]
    },
    "SETTLEMENT": {  // ← Add this
        color: "#9ca3af",
        icon: "settlement",
        svg: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55ZM7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41ZM16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z",
        faces: [
            { shade: 0.18, d: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55Z" },
            { shade: -0.12, d: "M7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41Z" },
            { shade: -0.34, d: "M16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z" },
        ]
    },
    // plain cube - 0.88 on every edge
    "FOOD": {
        color: "#f51d05",
        icon: "cube",
        svg: "M12 7.09L17.62 10.33L12 13.57L6.38 10.33ZM6.14 10.75L11.76 13.99L11.76 16.95L6.14 13.71ZM17.86 10.75L12.24 13.99L12.24 16.95L17.86 13.71Z",
        faces: [
            { shade: 0.18, d: "M12 7.09L17.62 10.33L12 13.57L6.38 10.33Z" },
            { shade: -0.12, d: "M6.14 10.75L11.76 13.99L11.76 16.95L6.14 13.71Z" },
            { shade: -0.34, d: "M17.86 10.75L12.24 13.99L12.24 16.95L17.86 13.71Z" },
        ]
    },
    // wide thin slab - 1.05 footprint, 0.35 thick
    "WATER": {
        color: "#057df5",
        icon: "slab",
        svg: "M12 7.45L18.79 11.37L12 15.3L5.21 11.37ZM4.97 11.79L11.76 15.71L11.76 16.56L4.97 12.64ZM19.03 11.79L12.24 15.71L12.24 16.56L19.03 12.64Z",
        faces: [
            { shade: 0.18, d: "M12 7.45L18.79 11.37L12 15.3L5.21 11.37Z" },
            { shade: -0.12, d: "M4.97 11.79L11.76 15.71L11.76 16.56L4.97 12.64Z" },
            { shade: -0.34, d: "M19.03 11.79L12.24 15.71L12.24 16.56L19.03 12.64Z" },
        ]
    },
    // low block - 0.97 footprint, 0.72 thick
    "TERRAIN": {
        color: "#05f76a",
        icon: "block",
        svg: "M12 7.02L18.24 10.62L12 14.22L5.76 10.62ZM5.52 11.04L11.76 14.64L11.76 16.96L5.52 13.36ZM18.48 11.04L12.24 14.64L12.24 16.96L18.48 13.36Z",
        faces: [
            { shade: 0.18, d: "M12 7.02L18.24 10.62L12 14.22L5.76 10.62Z" },
            { shade: -0.12, d: "M5.52 11.04L11.76 14.64L11.76 16.96L5.52 13.36Z" },
            { shade: -0.34, d: "M18.48 11.04L12.24 14.64L12.24 16.96L18.48 13.36Z" },
        ]
    },
    // sphere - radius 5 in viewBox units
    "NATURE": {
        color: "#1ABC9C",
        icon: "globe",
        svg: "M8.59 15.76A5 5 0 1 1 15.01 8.1Q12.95 12.9 8.59 15.76ZM8.99 16.1Q13.35 13.23 15.41 8.44A5 5 0 0 1 8.99 16.1Z",
        faces: [
            { shade: 0.18, d: "M8.59 15.76A5 5 0 1 1 15.01 8.1Q12.95 12.9 8.59 15.76Z" },
            { shade: -0.34, d: "M8.99 16.1Q13.35 13.23 15.41 8.44A5 5 0 0 1 8.99 16.1Z" },
        ]
    },
    // square pyramid - 1.00 footprint, 1.70 high
    "LANDMARK": {
        color: "#dff706",
        icon: "pyramid",
        svg: "M5.47 13.28L11.76 16.91L11.76 7.1ZM18.53 13.28L12.24 16.91L12.24 7.1Z",
        faces: [
            { shade: -0.12, d: "M5.47 13.28L11.76 16.91L11.76 7.1Z" },
            { shade: -0.34, d: "M18.53 13.28L12.24 16.91L12.24 7.1Z" },
        ]
    },

    // ========== POI TYPES (granular discovery icons) ==========
    "Restaurant": {
        color: "#f51d05",
        icon: "restaurant",
        svg: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55ZM7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41ZM16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z",
        faces: [
            { shade: 0.18, d: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55Z" },
            { shade: -0.12, d: "M7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41Z" },
            { shade: -0.34, d: "M16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z" },
        ]
    },
    "Cafe": {
        color: "#ff9800",
        icon: "cafe",
        svg: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55ZM7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41ZM16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z",
        faces: [
            { shade: 0.18, d: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55Z" },
            { shade: -0.12, d: "M7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41Z" },
            { shade: -0.34, d: "M16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z" },
        ]
    },
    "Supermarket": {
        color: "#ff6b35",
        icon: "supermarket",
        svg: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55ZM7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41ZM16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z",
        faces: [
            { shade: 0.18, d: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55Z" },
            { shade: -0.12, d: "M7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41Z" },
            { shade: -0.34, d: "M16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z" },
        ]
    },
    "Dentist": {
        color: "#e91e63",
        icon: "dentist",
        svg: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55ZM7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41ZM16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z",
        faces: [
            { shade: 0.18, d: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55Z" },
            { shade: -0.12, d: "M7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41Z" },
            { shade: -0.34, d: "M16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z" },
        ]
    },
    "Playground": {
        color: "#ffeb3b",
        icon: "playground",
        svg: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55ZM7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41ZM16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z",
        faces: [
            { shade: 0.18, d: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55Z" },
            { shade: -0.12, d: "M7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41Z" },
            { shade: -0.34, d: "M16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z" },
        ]
    },
    "Sports Complex": {
        color: "#4caf50",
        icon: "sports",
        svg: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55ZM7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41ZM16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z",
        faces: [
            { shade: 0.18, d: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55Z" },
            { shade: -0.12, d: "M7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41Z" },
            { shade: -0.34, d: "M16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z" },
        ]
    },
    "Bicycle Shop": {
        color: "#0288d1",
        icon: "bike",
        svg: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55ZM7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41ZM16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z",
        faces: [
            { shade: 0.18, d: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55Z" },
            { shade: -0.12, d: "M7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41Z" },
            { shade: -0.34, d: "M16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z" },
        ]
    },
    "Car Parking": {
        color: "#9ca3af",
        icon: "parking",
        svg: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55ZM7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41ZM16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z",
        faces: [
            { shade: 0.18, d: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55Z" },
            { shade: -0.12, d: "M7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41Z" },
            { shade: -0.34, d: "M16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z" },
        ]
    },
    "marker": {
        color: "#666666",
        icon: "marker",
        svg: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55ZM7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41ZM16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z",
        faces: [
            { shade: 0.18, d: "M12 5.95L16.51 8.55L12 11.15L7.49 8.55Z" },
            { shade: -0.12, d: "M7.25 8.97L11.76 11.57L11.76 18.01L7.25 15.41Z" },
            { shade: -0.34, d: "M16.75 8.97L12.24 11.57L12.24 18.01L16.75 15.41Z" },
        ]
    },
};

/** Mix a hex colour toward white (amount > 0) or black (amount < 0). */
export function shadeHex(hex: string, amount: number): string {
    const n = parseInt(hex.slice(1), 16);
    const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(c =>
        amount >= 0 ? c + (255 - c) * amount : c * (1 + amount)
    );
    return '#' + ch.map(c => Math.round(c).toString(16).padStart(2, '0')).join('').toUpperCase();
}

/**
 * Full <svg> markup for a category. Use for Mapbox HTML markers or [innerHTML].
 * `flat` renders the single-path silhouette instead of the shaded faces.
 */
export function buildIconMarkup(
    key: string,
    size = 32,
    opts: { flat?: boolean; color?: string } = {}
): string {
    const cfg = ICON_REGISTRY[key];
    if (!cfg) return '';
    const base = opts.color ?? cfg.color;
    const inner = opts.flat
        ? `<path fill="${base}" d="${cfg.svg}"/>`
        : cfg.faces.map(f => `<path fill="${shadeHex(base, f.shade)}" d="${f.d}"/>`).join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${ICON_VIEW_BOX}" `
        + `width="${size}" height="${size}">${inner}</svg>`;
}